import { getAuthToken } from "Utils/common";
import { marketingRichTextImageUrl } from "Utils/constant";

const FILE_KEY_ATTRIBUTE = "data-s3-file-key";
const imageCache = new Map<string, string>();
const pendingRequests = new Map<string, Promise<string | null>>();

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Image read failed"));
    reader.readAsDataURL(blob);
  });
}

async function loadImage(fileKey: string) {
  const normalized = fileKey.trim();
  if (!normalized) {
    return null;
  }
  if (imageCache.has(normalized)) {
    return imageCache.get(normalized) || null;
  }
  if (pendingRequests.has(normalized)) {
    return pendingRequests.get(normalized)!;
  }
  const token = getAuthToken();
  const request = fetch(
    `${marketingRichTextImageUrl}?fileKey=${encodeURIComponent(normalized)}`,
    token ? { headers: { Authorization: token } } : undefined
  )
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Rich text image request failed: ${response.status}`);
      }
      const dataUrl = await blobToDataUrl(await response.blob());
      imageCache.set(normalized, dataUrl);
      return dataUrl;
    })
    .catch(() => null)
    .finally(() => pendingRequests.delete(normalized));
  pendingRequests.set(normalized, request);
  return request;
}

export async function hydrateMarketingRichText(element: HTMLElement | null) {
  if (!element) {
    return;
  }
  const images = Array.from(
    element.querySelectorAll<HTMLImageElement>(`img[${FILE_KEY_ATTRIBUTE}]`)
  );
  await Promise.all(
    images.map(async (image) => {
      const fileKey = image.getAttribute(FILE_KEY_ATTRIBUTE)?.trim();
      if (!fileKey) {
        return;
      }
      const dataUrl = await loadImage(fileKey);
      if (dataUrl && image.getAttribute(FILE_KEY_ATTRIBUTE)?.trim() === fileKey) {
        image.src = dataUrl;
      }
    })
  );
}

export function buildActivityCartCache(activity: any) {
  return {
    activityBasicType: activity?.activityBasicType,
    activityTypeName: activity?.activityTypeName,
    activityTypeDescription: activity?.activityTypeDescription,
    exchangeTypeName: activity?.exchangeTypeName,
    exchangeTypeDescription: activity?.exchangeTypeDescription,
  };
}
